/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Dec 13 2023
 **/

import { BaseService, Response, ReturnData } from "@/frameworks/libraries/restservice";

export default class FileAPI extends BaseService {
	constructor() {
		super();
	}

	/**
	 * @summary 좌측 트리(스페이스 & 채팅) 조회 - API (/api/filebox/chat_simple_rooms) 참조
	 * @request GET:/filebox/folders
	 */
	public get_all_folders = (): Promise<ReturnData<FileBox.Folders.GetFoldersResponse>> =>
		this.get<FileBox.Folders.GetFoldersResponse>("/filebox/folders");

	/**
	 * @summary 내 파일함 폴더 (이동할 폴더 목록) 조회
	 * @request GET:/filebox/folders/my-folders
	 */
	public get_my_folders = (
		options?: FileBox.Folders.GetMyFoldersRequest
	): Promise<ReturnData<FileBox.Folders.GetMyFoldersResponse>> =>
		this.get<FileBox.Folders.GetMyFoldersResponse>(
			`/filebox/folders/my-folders${options && options.id ? `/${options.id}` : ""}`
		);

	/**
	 * @summary 폴더 & 파일 조회
	 * @request GET:/filebox/folders/{id}
	 */
	public get_folder_file_list = ({
		id,
		...rest
	}: FileBox.Folders.GetFolderFileListRequest): Promise<ReturnData<FileBox.Folders.GetFolderFileListResponse>> =>
		this.get(`/filebox/folders/${id}`, rest);

	/**
	 * @summary 전체 공유 파일함(스페이스, 채팅) 조회
	 * @request GET:/filebox/folders/all
	 */
	public get_all_folder_file_list = ({
		...rest
	}: FileBox.Folders.GetAllFolderFileRequest): Promise<ReturnData<FileBox.Folders.GetAllFolderFileResponse>> =>
		this.get(`/filebox/folders/all`, rest);

	/**
	 * @summary 폴더 추가
	 * @request POST:/filebox/folders/{id}
	 */
	public create_folder = ({
		id,
		...rest
	}: FileBox.Folders.CreateFolderRequest): Promise<ReturnData<FileBox.Folders.CreateFolderResponse>> =>
		this.post(`/filebox/folders/${id}`, rest);

	/**
	 * @summary 폴더명 변경
	 * @request PUT:/filebox/folders/{id}
	 */
	public rename_folder = ({
		id,
		...rest
	}: FileBox.Folders.RenameFolderRequest): Promise<ReturnData<FileBox.Folders.RenameFolderResponse>> =>
		this.put(`/filebox/folders/${id}`, rest);

	/**
	 * @summary 폴더 삭제
	 * @request DELETE:/filebox/folders/{id}
	 */
	public delete_folder = ({
		id
	}: FileBox.Folders.DeleteFolderRequest): Promise<ReturnData<FileBox.Folders.DeleteFolderResponse>> =>
		this.delete(`/filebox/folders/${id}`);

	/**
	 * @summary 폴더 이동
	 * @request PUT:/filebox/folders/{id}/move
	 */
	public move_folder = ({
		id,
		...rest
	}: FileBox.Folders.MoveFolderRequest): Promise<ReturnData<FileBox.Folders.MoveFolderResponse>> =>
		this.put(`/filebox/folders/${id}/move`, rest);

	/**
	 * @summary 폴더 다운로드
	 * @request GET:/filebox/folders/{id}/download
	 */
	public download_folder = ({
		id
	}: FileBox.Folders.DownloadFolderRequest): Promise<ReturnData<FileBox.Folders.DownloadFolderResponse>> =>
		this.get(`/filebox/folders/${id}/download`, {}, { responseType: "blob" });

	/**
	 * @summary 파일명 변경
	 * @request PUT:/filebox/files/{id}
	 */
	public rename_file = ({
		id,
		...rest
	}: FileBox.Files.RenameFileRequest): Promise<ReturnData<FileBox.Files.RenameFileResponse>> =>
		this.put(`/filebox/files/${id}`, rest);

	/*
	 * @summary 파일 상세 정보
	 * @request GET:/files/{id}/detail
	 */
	public get_file_detail = ({
		id
	}: FileBox.Files.GetFileDetailRequest): Promise<ReturnData<FileBox.Files.GetFileDetailResponse>> =>
		this.get(`/filebox/files/${id}/detail`);

	/**
	 * @summary 미팅 회의 파일목록 조회
	 * @request GET:/files/conferences/{id}
	 * @param id
	 */
	public get_file_list_in_conference = ({
		id
	}: FileBox.Files.GetFileListInConferenceRequest): Promise<
		ReturnData<FileBox.Files.GetFileListInConferenceResponse>
	> => this.get(`/filebox/files/conferences/${id}`);

	/**
	 * @summary 사본 만들기
	 * @request POST:/filebox/files/{id}/copy
	 */
	public copy_file = ({
		id,
		...rest
	}: FileBox.Files.CopyFileRequest): Promise<ReturnData<FileBox.Files.CopyFileResponse>> =>
		this.post(`/filebox/files/${id}/copy`, rest);

	/**
	 * @summary 파일 삭제
	 * @request DELETE:/filebox/files/{id}
	 */
	public delete_file = ({
		id
	}: FileBox.Files.DeleteFileRequest): Promise<ReturnData<FileBox.Files.DeleteFileResponse>> =>
		this.delete(`/filebox/files/${id}`);

	/**
	 * @summary 파임함 파일 업로드
	 * @request POST:/filebox/upload/{id}
	 */
	public upload_file = ({
		id,
		...rest
	}: FileBox.Upload.UploadRequest): Promise<ReturnData<FileBox.Upload.UploadResponse>> =>
		this.post(`/filebox/upload/${id}`, rest, {
			headers: { "Content-Type": "multipart/form-data; boundary=boundary" }
		});

	/**
	 * @summary 링크 등록
	 * @request POST:/filebox/links/{id}
	 */
	public save_link = ({
		id,
		...rest
	}: FileBox.Link.SaveLinkRequest): Promise<ReturnData<FileBox.Link.SaveLinkResponse>> =>
		this.post(`/filebox/links/${id}`, rest);

	/**
	 * @summary 링크 삭제
	 * @request DELETE:/filebox/links/{id}
	 */
	public remove_link = ({
		id
	}: FileBox.Link.RemoveLinkRequest): Promise<ReturnData<FileBox.Link.RemoveLinkResponse>> =>
		this.delete(`/filebox/links/${id}`);

	/**
	 *
	 * @summary 링크 사본 만들기
	 * @request POST:/filebox/links/{id}/copy
	 */
	public copy_link = ({
		id,
		...rest
	}: FileBox.Link.CopyLinkRequest): Promise<ReturnData<FileBox.Link.CopyLinkResponse>> =>
		this.post(`/filebox/links/${id}/copy`, rest);

	/**
	 * @summary 파일명 중복 확인
	 * @request GET:/filebox/files/{id}/exists-filename
	 */
	public exists_filename = ({
		id,
		...rest
	}: FileBox.Files.ExistFilenameRequest): Promise<ReturnData<FileBox.Files.ExistFilenameResponse>> =>
		this.get(`/filebox/files/${id}/exists-filename`, rest);

	/**
	 * @summary 파일 이동
	 * @request PUT:/filebox/files/{id}/move
	 */
	public move_file = ({
		id,
		...rest
	}: FileBox.Files.FileMoveRequest): Promise<ReturnData<FileBox.Files.FileMoveResponse>> =>
		this.put(`/filebox/files/${id}/move`, rest);

	/**
	 * @summary 파일 다운로드
	 * @request GET:/filebox/files/{id}
	 */
	public download_files = ({
		id,
		...rest
	}: FileBox.Files.DownloadFilesRequest): Promise<ReturnData<FileBox.Files.DownloadFilesResponse>> =>
		this.get(`/filebox/files/${id}`, rest, { responseType: "blob" });

	/**
	 * @summary
	 * @request POST:/filebox/doc_image
	 */
	public convert_doc_to_image = ({
		fileId
	}: FileBox.DocImage.ConvertDocToImageRequest): Promise<ReturnData<FileBox.DocImage.ConvertDocToImageResponse>> =>
		this.post(`/filebox/doc_image`, { fileId });
	/**
	 * @summary
	 * @request GET:/filebox/doc_image/{id}
	 */
	public download_doc_to_image = ({
		id,
		...rest
	}: FileBox.DocImage.DownloadDocImageRequest): Promise<ReturnData<FileBox.DocImage.DownloadDocImageResponse>> =>
		this.get(`/filebox/doc_image/${id}`, rest, { responseType: "blob" });

	/**
	 * @summary
	 * @request GET:/filebox/doc_image/{id}/thumbnail
	 */
	public download_doc_thumbnail = ({
		id,
		...rest
	}: FileBox.DocImage.DownloadDocThumbnailRequest): Promise<
		ReturnData<FileBox.DocImage.DownloadDocThumbnailResponse>
	> => this.get(`/filebox/doc_image/${id}/thumbnail`, rest, { responseType: "blob" });

	/**
	 * @summary
	 * @request GET:/filebox/doc_image/{id}/thumbnail
	 */
	public progress_doc_image = ({
		id
	}: FileBox.DocImage.ProgressDocImageRequest): Promise<ReturnData<FileBox.DocImage.ProgressDocImageResponse>> =>
		this.get(`/filebox/doc_image/${id}/progress`);

	/**
	 * @summary
	 * @request DELETE:/tus/{tusId}
	 */
	public tus_abort = ({ tusId }: FileBox.Tus.AbortRequest): Promise<ReturnData<FileBox.Tus.AbortResponse>> =>
		this.delete(`/tus/${tusId}`);
}
